import i18n from '@/util/config/i18n'
import { goToPlansFromAnywhere } from '@/modules/team/util/planNavigation'
import { TRIAL_SOFT_LOCKED_ERROR_CODE } from '@/modules/featurerestriction/errorCodes'

/** Stable BE error code returned by the soft-lock interceptor (403). */
export { TRIAL_SOFT_LOCKED_ERROR_CODE }

/** Marks the Stripe success return of a soft-lock upgrade so the app can drop the bar and celebrate. */
export const TRIAL_UPGRADED_QUERY_PARAM = 'trialUpgraded'

/**
 * Where Stripe returns a soft-locked team after a successful upgrade. Framework.vue watches for the
 * flag and swaps the bar for the success notice, so the plans-page checkout has to carry this route.
 */
export const TRIAL_UPGRADE_SUCCESS_ROUTE = `/recently-viewed?${TRIAL_UPGRADED_QUERY_PARAM}=true`
const SNACKBAR_DEDUPE_KEY = 'trial-soft-locked'

/**
 * "The trial-ended explainer was already shown" marker, per team.
 *
 * sessionStorage plus a wipe on logout makes that once per *login* rather than once per browser
 * session: a soft-locked team should get the upgrade pitch every time they sign in, but not again on
 * every route change or page reload inside that session. Per team because one browser can switch
 * between a locked team and a paid one.
 */
const TRIAL_ENDED_ONBOARDING_SEEN_PREFIX = 'notch:trial-ended-onboarding-seen:'

export function hasSeenTrialEndedOnboarding(teamId: string): boolean {
  try {
    return window.sessionStorage.getItem(`${TRIAL_ENDED_ONBOARDING_SEEN_PREFIX}${teamId}`) === 'true'
  } catch {
    // sessionStorage may be unavailable; showing the explainer again beats crashing the shell.
    return false
  }
}

export function markTrialEndedOnboardingSeen(teamId: string): void {
  try {
    window.sessionStorage.setItem(`${TRIAL_ENDED_ONBOARDING_SEEN_PREFIX}${teamId}`, 'true')
  } catch {
    // Without storage the explainer reappears on the next navigation — acceptable, and not fatal.
  }
}

/** Called on logout, for every team, so the next login shows the explainer again. */
export function clearTrialEndedOnboardingSeen(): void {
  try {
    const storage = window.sessionStorage
    // Collect first: removing while iterating shifts the indices. `key(i)` rather than
    // Object.keys, which does not enumerate Storage entries everywhere.
    const keys: string[] = []
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)
      if (key?.startsWith(TRIAL_ENDED_ONBOARDING_SEEN_PREFIX)) {
        keys.push(key)
      }
    }
    keys.forEach(key => storage.removeItem(key))
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

export function isTrialSoftLockedError(error: any): boolean {
  return error?.response?.status === 403 && error?.response?.data?.code === TRIAL_SOFT_LOCKED_ERROR_CODE
}

/**
 * NS-205 soft-lock safety net. Gated actions are hidden for soft-locked teams, so a 403
 * `trial-soft-locked` means one slipped through (or the trial expired mid-session). Explain it with an
 * upgrade nudge instead of a silent failure, and resync the team so the soft-lock bar and the
 * permission gating catch up. Notch trial cohort only — legacy FREE never hits this code.
 */
export async function handleTrialSoftLockedError(): Promise<void> {
  const { useSnackbarStore } = await import('@/modules/global/stores/SnackbarStore')
  const { useTeamStore } = await import('@/modules/team/stores/TeamStore')

  useSnackbarStore().enqueue({
    label: i18n.t('trialLocked.actionBlocked') as string,
    variant: 'warning',
    leadingIcon: true,
    dedupeKey: SNACKBAR_DEDUPE_KEY,
    buttonLabel: i18n.t('trialLocked.upgrade') as string,
    onButtonClick: () => {
      void goToPlansFromAnywhere()
    }
  })

  const teamStore = useTeamStore()
  if (!teamStore.isTrialLocked) {
    try {
      await teamStore.fetchCurrentTeam()
    } catch {
      // Keep the current state if the resync fails; the snackbar already explained the block.
    }
  }
}
